import React from "react";
import { Banner, Button, FormField, Input } from "@/ds";
import { cockpit, type RequestDetail } from "@/api";
import { workspaceView } from "../../api/workspace-view";
import { useT } from "../../i18n";
import { Icon } from "@/lib";
import styles from "./sol-guidance.module.css";
import { taskActionLabel } from "./task-presentation";

/** The request details dialog focuses here when a task stands between the draft and approval. */
export const WORKSPACE_TASKS_ID = "workspace-tasks";

const DECISIONS = ["approved", "refused"] as const;

export function WorkspaceTasks({ data, disabled, reload, guided, afterTasks, taskAction, headingTask }: {
  data: RequestDetail; disabled: boolean; reload: () => Promise<unknown>; guided?: boolean;
  afterTasks?: React.ReactNode; taskAction?: { id: string; content: React.ReactNode };
  headingTask?: { id: string; title: string };
}) {
  const t = useT(), view = workspaceView(data);
  const [reasons, setReasons] = React.useState<Record<string, string>>({});
  const [busy, setBusy] = React.useState(false), [error, setError] = React.useState<string | null>(null);
  if (!view.tasks.length && !view.questions.length && !afterTasks) return null;
  // This code-owned correction instruction applies equally to every informational task.
  // Say it once after the last such task; task descriptions and resolutions stay untouched.
  const lastInformationalTask = guided ? [...view.tasks].reverse().find(task => !task.blocking && !task.operator_action)?.id : null;
  const questions = view.questions.length > 0 && (guided
    ? <details className={styles.questions} open>
      <summary className={styles.summary}>
        <Icon name="reply" size={20} />
        <span className={styles.sectionTitle}>{t("workspace.questions")}</span>
        <span className={styles.count}>{view.questions.length}</span>
        <span className={styles.chevron}><Icon name="chevronDown" size={16} /></span>
      </summary>
      <ol className={styles.questionList}>{view.questions.map((q, index) => <li key={q.id}>
        <span className={styles.questionNumber} aria-hidden="true">{index + 1}</span><span>{q.text}</span>
      </li>)}</ol>
    </details>
    : <details open><summary>{t("workspace.questions")}</summary><ul>{view.questions.map(q => <li key={q.id}>{q.text}</li>)}</ul></details>);
  return <section id={WORKSPACE_TASKS_ID} tabIndex={-1} className={guided ? styles.tasks : "ws-secondary-section stack-sm"} aria-label={t("workflow.tasks")}>
    {error && <Banner tone="danger" title={t("common.error")}>{error}</Banner>}
    {!guided && questions}
    {view.tasks.map(task => {
      // The server says what may be recorded: a decision, or "Done" for a point of an offer. A reply
      // without an offer confirms its open points when it is sent (#2516), so none is clicked here.
      const decide = task.operator_action === "decide", done = task.operator_action === "done", confirm = task.reply_confirmable && task.open;
      const actions = decide ? DECISIONS : done ? (["approved"] as const) : [];
      const form = actions.length > 0 && <>
        <FormField label={t("workflow.reason")}><Input value={reasons[task.id] ?? ""} maxLength={2000} disabled={disabled || busy}
          onChange={e => setReasons({ ...reasons, [task.id]: e.target.value })} /></FormField>
        <div className="row-wrap">{actions.map(action => <Button size="sm" key={action}
          disabled={disabled || busy} onClick={async () => {
            setBusy(true); setError(null);
            try { await cockpit.resolveWorkflowTask(data.request.id, { result_id: data.workflow!.result_id!, task_id: task.id,
              expected_draft_id: data.draft?.id, expected_draft_version: data.draft?.version, expected_revision: view.revision, action, reason: reasons[task.id]! });
              // The form stays for a changed decision; the reason just recorded must not ride along.
              setReasons(current => ({ ...current, [task.id]: "" })); await reload(); }
            catch (e) { setError(e instanceof Error ? e.message : t("common.error")); }
            finally { setBusy(false); }
          }}>{t(taskActionLabel(task.disagreement ? "fact_disagreement" : task.reason_code, action, done))}</Button>)}</div>
      </>;
      const titleKey = `workflow.task.${task.disagreement ? "fact_disagreement" : task.reason_code}`;
      const headingAlreadyShown = headingTask?.id === task.id && headingTask.title === titleKey;
      return <section key={task.id} className={guided ? styles.task : "stack-sm"} data-task-open={task.open || undefined}>
        {guided && <span className={styles.taskIcon}><Icon name="fileText" size={20} /></span>}
        <div className={guided ? styles.taskBody : "stack-sm"}>
        {!headingAlreadyShown && <strong className={guided ? styles.sectionTitle : undefined}>{t(titleKey)}</strong>}
        <p className={guided ? styles.description : undefined}>{task.disagreement ? t(`field.${task.disagreement.field_path}`) : task.description}</p>
        {taskAction?.id === task.id && taskAction.content}
        {!confirm && (!guided || task.blocking || task.open) && <small>{t(task.open ? "workflow.blocking" : task.blocking ? "workflow.settled" : "workflow.information")}</small>}
        {task.decision && <p>{t(`workflow.resolution.${task.decision.action}`)}: {task.decision.reason}</p>}
        {/* Behind a decision the confirmation speaks of the reply alone; the point is not open any more. */}
        {task.resolution && <p>{t(task.decision ? "workflow.resolution.reply_confirmed.afterDecision" : `workflow.resolution.${task.resolution.action}`)}: {task.resolution.reason}</p>}
        {/* A refusal settles the matter, not the reply: the draft was written before it. */}
        {task.resolution && task.open && !confirm && <p>{t("workflow.afterRefusal")}</p>}
        {/* A point settled at send remains business work, not a prerequisite for this reply. */}
        {task.blocking && task.open && !confirm && !decide && !done && <p>{t(`workflow.correct.${task.reason_code}`, undefined, t("workflow.sourceAction"))}</p>}
        {!task.blocking && !task.operator_action && (!guided || task.id === lastInformationalTask) && <p>{t("workflow.sourceAction")}</p>}
        {confirm && <p>{t("sendReady.team.atSend")}</p>}
        {/* The newest record counts, so a settled decision stays changeable; it folds away so that
            a settled task does not look like one that still waits. */}
        {task.resolution && !task.open ? form && <details><summary>{t("workflow.changeDecision")}</summary>{form}</details> : form}
        </div>
      </section>;
    })}
    {afterTasks}
    {guided && questions}
  </section>;
}
